<template>
  <b-modal
    v-model="visible"
    :title="title"
    size="xl"
    hide-footer
    no-close-on-backdrop
  >
    <!-- Header con acciones -->
    <div class="d-flex justify-content-between align-items-center mb-2">
      <div>
        <b-badge variant="light-info">
          {{ customers.length }} cliente(s)
        </b-badge>
        <small
          v-if="route && route.user && route.user.name"
          class="text-muted ml-2"
        >
          Vendedor: <strong>{{ route.user.name }}</strong>
        </small>
        <small
          v-else-if="route"
          class="text-muted ml-2"
        >
          Sin vendedor asignado
        </small>
      </div>
      <b-button
        variant="primary"
        size="sm"
        @click="openAttachModal"
      >
        <feather-icon
          icon="UserPlusIcon"
          size="14"
          class="mr-25"
        />
        Agregar clientes
      </b-button>
    </div>

    <b-form-input
      v-model="searchQuery"
      class="mb-2"
      placeholder="Buscar por nombre o NIT..."
      size="sm"
    />

    <b-table
      hover
      responsive
      small
      :busy="loading"
      :items="filteredCustomers"
      :fields="fields"
      show-empty
      empty-text="No hay clientes en esta ruta."
    >
      <template #table-busy>
        <div class="text-center my-2">
          <b-spinner />
        </div>
      </template>

      <template #cell(name)="data">
        <b-link
          class="font-weight-bold text-primary"
          @click="goToCustomer(data.item.id)"
        >
          {{ data.item.name }}
        </b-link>
      </template>

      <template #cell(address)="data">
        <span v-if="data.item.address">{{ data.item.address }}</span>
        <small
          v-else
          class="text-muted"
        >Sin dirección</small>
      </template>

      <template #cell(actions)="data">
        <b-button
          variant="flat-danger"
          size="sm"
          class="p-50"
          title="Quitar de la ruta"
          @click="detachCustomer(data.item)"
        >
          <feather-icon
            icon="UserMinusIcon"
            size="14"
          />
        </b-button>
      </template>
    </b-table>

    <!-- Sub-modal: Agregar clientes sin ruta -->
    <b-modal
      v-model="attachModalVisible"
      title="Agregar clientes a la ruta"
      size="lg"
      hide-footer
      no-close-on-backdrop
    >
      <p class="text-muted small">
        Selecciona clientes que actualmente no tienen ruta para asignarlos a
        <strong v-if="route">{{ route.name }}</strong>.
      </p>

      <b-form-input
        v-model="attachSearchQuery"
        class="mb-2"
        placeholder="Buscar cliente por nombre o NIT..."
        size="sm"
        @input="searchAvailable"
      />

      <div
        v-if="loadingAvailable"
        class="text-center my-2"
      >
        <b-spinner small />
      </div>
      <div
        v-else-if="availableCustomers.length === 0"
        class="text-muted text-center py-2"
      >
        No hay clientes sin ruta {{ attachSearchQuery ? 'que coincidan con la búsqueda' : '' }}.
      </div>
      <b-list-group
        v-else
        class="available-list"
      >
        <b-list-group-item
          v-for="c in availableCustomers"
          :key="c.id"
          class="d-flex align-items-center"
        >
          <b-form-checkbox
            v-model="selectedIds"
            :value="c.id"
            class="mr-2"
          />
          <div class="flex-grow-1">
            <div>
              <strong>{{ c.name }}</strong>
              <small class="text-muted ml-1">NIT: {{ c.nit }}</small>
            </div>
            <small
              v-if="c.address"
              class="text-muted"
            >{{ c.address }}</small>
          </div>
        </b-list-group-item>
      </b-list-group>

      <div class="d-flex justify-content-end mt-2">
        <b-button
          variant="outline-secondary"
          class="mr-1"
          @click="attachModalVisible = false"
        >
          Cancelar
        </b-button>
        <b-button
          variant="primary"
          :disabled="selectedIds.length === 0 || attaching"
          @click="confirmAttach"
        >
          {{ attaching ? 'Asignando...' : `Asignar ${selectedIds.length} cliente(s)` }}
        </b-button>
      </div>
    </b-modal>
  </b-modal>
</template>

<script>
import axios from '@axios'

export default {
  name: 'CustomerRouteCustomersModal',

  props: {
    show: {
      type: Boolean,
      required: true,
    },
    route: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      customers: [],
      loading: false,
      searchQuery: '',

      attachModalVisible: false,
      availableCustomers: [],
      loadingAvailable: false,
      attachSearchQuery: '',
      attachSearchTimeout: null,
      selectedIds: [],
      attaching: false,

      fields: [
        { key: 'name', label: 'Cliente' },
        { key: 'nit', label: 'NIT' },
        { key: 'address', label: 'Dirección' },
        { key: 'actions', label: 'Acciones', tdClass: 'text-right' },
      ],
    }
  },

  computed: {
    visible: {
      get() {
        return this.show
      },
      set(value) {
        this.$emit('update:show', value)
      },
    },
    title() {
      return this.route ? `Clientes de ${this.route.name}` : 'Clientes de la ruta'
    },
    filteredCustomers() {
      if (!this.searchQuery) return this.customers
      const q = this.searchQuery.toLowerCase()
      return this.customers.filter(
        c => (c.name || '').toLowerCase().includes(q)
          || (c.nit || '').toLowerCase().includes(q),
      )
    },
  },

  watch: {
    show(newVal) {
      if (newVal && this.route) {
        this.loadCustomers()
      }
    },
  },

  methods: {
    loadCustomers() {
      this.loading = true
      axios
        .get(`customer-route/${this.route.id}/customers`)
        .then(response => {
          this.customers = response.data.data || []
        })
        .catch(error => {
          this.showErrors(error)
        })
        .finally(() => {
          this.loading = false
        })
    },

    goToCustomer(id) {
      this.visible = false
      this.$router.push({ name: 'contacts-customers', params: { id } })
    },

    detachCustomer(customer) {
      this.$bvModal
        .msgBoxConfirm(
          `¿Quitar a ${customer.name} de esta ruta? El cliente perderá también al vendedor de la ruta.`,
          {
            title: 'Quitar cliente',
            size: 'sm',
            okVariant: 'danger',
            okTitle: 'Sí',
            cancelTitle: 'No',
            centered: true,
          },
        )
        .then(value => {
          if (!value) return
          axios
            .delete(`customer-route/${this.route.id}/customers/${customer.id}`)
            .then(() => {
              this.makeToast('success', 'Cliente removido', `${customer.name} ya no pertenece a esta ruta.`)
              this.loadCustomers()
              this.$emit('changed')
            })
            .catch(error => {
              this.showErrors(error)
            })
        })
    },

    openAttachModal() {
      this.selectedIds = []
      this.attachSearchQuery = ''
      this.attachModalVisible = true
      this.loadAvailable()
    },

    loadAvailable() {
      this.loadingAvailable = true
      axios
        .get('customer-route-available-customers', {
          params: { query: this.attachSearchQuery },
        })
        .then(response => {
          this.availableCustomers = response.data.data || []
        })
        .catch(error => {
          this.showErrors(error)
        })
        .finally(() => {
          this.loadingAvailable = false
        })
    },

    searchAvailable() {
      clearTimeout(this.attachSearchTimeout)
      this.attachSearchTimeout = setTimeout(() => {
        this.loadAvailable()
      }, 300)
    },

    confirmAttach() {
      this.attaching = true
      axios
        .post(`customer-route/${this.route.id}/customers`, {
          customer_ids: this.selectedIds,
        })
        .then(response => {
          const attached = response.data.data && response.data.data.attached
          this.makeToast('success', 'Clientes asignados', `${attached} cliente(s) asignados a ${this.route.name}.`)
          this.attachModalVisible = false
          this.loadCustomers()
          this.$emit('changed')
        })
        .catch(error => {
          this.showErrors(error)
        })
        .finally(() => {
          this.attaching = false
        })
    },

    makeToast(variant, title, message) {
      this.$bvToast.toast(message, { title, variant, solid: true })
    },
  },
}
</script>

<style scoped>
.available-list {
  max-height: 360px;
  overflow-y: auto;
}
</style>
